import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { shallowEqual, useSelector } from "react-redux";

import { ALL_CHART_TYPES } from "constants/chart.constants";
import { RootState } from "store/rootReducer";
import {
  selectWellCardChartNormalizeBy,
  useWellCardPanelStore
} from "stores/wellCardPanel";
import { getProductType } from "utils";

import { postMultiPhase } from "api/multiProduct";

import { ChartSeries, IChartResult } from "models/model";
import { INormalizeByInput } from "models/normalizeBy";

import {
  IMultiPhaseInput,
  IMultiPhaseSettingInput
} from "components/multiphase-chart/models/shared.models";
import { getDefaultProductStylesMergedWithCustomStyles } from "components/multiphase-chart/util/productStyles";

import {
  ArrayProductKey,
  CalendarMonth,
  DEFAULT_ARRAY_UNITS,
  ProductionArrayUnits,
  calendarFromSeriesX,
  deriveArrayUnit,
  pickHistoricalSeries,
  resolveYAxisTitle,
  timeAxisValueToMs,
  volumeFromCalendarDayRate
} from "../utils/productionArray";

export interface ProductionArrayRow {
  /** The response's own x value for this row, unique across the table. */
  readonly date: number | string;
  readonly monthsIndex: number;
  readonly calendar: Maybe<CalendarMonth>;
  readonly oilVolume: Maybe<number>;
  readonly oilRate: Maybe<number>;
  readonly gasVolume: Maybe<number>;
  readonly gasRate: Maybe<number>;
  readonly waterVolume: Maybe<number>;
  readonly waterRate: Maybe<number>;
}

export interface ProductionArrayResult {
  readonly rows: readonly ProductionArrayRow[];
  /** The unit each column's numbers are in, read off the response's axes. */
  readonly units: ProductionArrayUnits;
  /**
   * One signal for the chart, the array and the Performance table. It comes
   * from the rate response alone so the three blocks cannot disagree while
   * their own fetches settle.
   */
  readonly hasProduction: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
  /**
   * The request never ran because no filter id arrived. Folded into `isError`,
   * because to every gate downstream it is the same thing — no numbers — but
   * the copy it deserves is not "the request failed".
   */
  readonly filterUnavailable: boolean;
  /** The rows still describe the previous target while the new one loads. */
  readonly isPreviousData: boolean;
  /**
   * The normalized rows failed while the unnormalized answer stands. Kept off
   * `isError`: that unmounts the chart, which drops the user's normalize choice.
   */
  readonly rowsFailed: boolean;
  /**
   * The service could not normalize this well (it has no value for the field),
   * so its numbers came back raw under a normalized title.
   */
  readonly notNormalizable: boolean;
  /** Display name of the field the rows are normalized by, or null when they are raw. */
  readonly normalizedBy: Maybe<string>;
  readonly refetch: () => void;
}

const PRODUCTS: readonly ArrayProductKey[] = ["oil", "gas", "water"];

/**
 * The three request inputs, read straight from Redux. `useChartDependencies`
 * returns the same values, but it also subscribes to the four pointer-frequency
 * hover atoms — a cost the whole panel would pay on every chart hover for three
 * fields that never change during one.
 */
const selectRequestInputs = (state: RootState) => ({
  filterId: state.filter.filterId,
  userForecastSettings: state.app.userForecastSettings,
  forecastMode: state.app.forecastMode
});

const NOT_NORMALIZED: INormalizeByInput = {
  useNormalizeBy: false,
  field: "",
  displayName: "",
  per: 0,
  unit: "",
  defaultUnit: "",
  useMultilinearNormalization: false,
  threshold: 0,
  lowerScalar: 1,
  higherScalar: 1
};

const buildRequest = (
  uwi: string,
  filterId: string,
  chartType: number,
  chartSetting: Omit<IMultiPhaseSettingInput, "chartType">,
  userForecastSettings: IMultiPhaseInput["userForecastSettings"]
): IMultiPhaseInput => ({
  filterId,
  products: [
    { axis: "primary", name: getProductType("Oil + Cond").key },
    { axis: "primary", name: getProductType("Gas").key },
    { axis: "primary", name: getProductType("Water").key }
  ],
  uwi,
  chartStyles: { product: getDefaultProductStylesMergedWithCustomStyles([]) },
  userForecastSettings,
  userForecast: [],
  chartSetting: { ...chartSetting, chartType },
  useBackfit: false,
  combineSameUnitAxis: false
});

/**
 * One product's numbers in the column's unit, keyed by the date the response
 * put on its x axis.
 */
type AxisKey = number | string;
type ByDate = Map<AxisKey, Maybe<number>>;

/**
 * Key a series on the date the response reported, in the column's own unit.
 * The service fills every month between the first and the last it reports, so
 * a month with no record arrives as a zero and the table prints one, the same
 * number the chart draws. A value the service sends as null stays null.
 */
const keyByDate = (series: Maybe<ChartSeries>, factor: number): ByDate => {
  const byDate: ByDate = new Map();
  const x = series?.x ?? [];
  const y = series?.y ?? [];
  for (let i = 0; i < x.length && i < y.length; i++) {
    byDate.set(x[i], y[i] == null ? null : y[i] * factor);
  }
  return byDate;
};

/** Whichever column, the same lookup: absent and reported-empty are both null. */
const valueAt = (byDate: ByDate, date: AxisKey): Maybe<number> => {
  const found = byDate.get(date);
  return found === undefined ? null : found;
};

interface ParsedArrayData {
  readonly rates: readonly ByDate[];
  readonly volumes: readonly ByDate[];
  readonly monthsIndex: readonly number[];
  /** One x value per row, in the rate response's own date axis. */
  readonly dates: readonly AxisKey[];
  /** The month each row falls in, read from the same x value. */
  readonly calendars: readonly Maybe<CalendarMonth>[];
  readonly hasProduction: boolean;
  readonly units: ProductionArrayUnits;
  /** Normalization was asked for and the service had no divisor for the well. */
  readonly notNormalizable?: boolean;
  /** The well these rows were fetched for, so a previous well's can be told apart. */
  readonly uwi?: string;
  /** The normalize field these rows were divided by, read with them rather than from the chart. */
  readonly normalizedBy?: string;
}

/**
 * The response's layout is what says which unit its numbers are in, so it is
 * read here rather than thrown away with the rest of the envelope. It answers
 * per product, so each column takes its unit from its own series' axis.
 */
export const parseResponse = (rate: IChartResult): ParsedArrayData => {
  const rateSeries = PRODUCTS.map((p) => pickHistoricalSeries(rate?.series, p));

  const rateUnits = PRODUCTS.map((product, i) =>
    deriveArrayUnit(
      resolveYAxisTitle(rateSeries[i], rate?.layout),
      DEFAULT_ARRAY_UNITS[product]
    )
  );

  const rates = rateSeries.map((series, i) => keyByDate(series, rateUnits[i].factor));

  // Built from the keys the values are under, not from the raw axis: an x with
  // no y behind it would otherwise draw a row of dashes and take a month
  // number off every row below it. A value the axis reader cannot place is
  // left out for the same reason.
  const dates = Array.from(new Set(rates.flatMap((byDate) => [...byDate.keys()])))
    .map((date) => ({ date, ms: timeAxisValueToMs(date) }))
    .filter((keyed): keyed is { date: AxisKey; ms: number } => keyed.ms !== null)
    .sort((a, b) => a.ms - b.ms)
    .map((keyed) => keyed.date);

  const calendars = dates.map((date) => calendarFromSeriesX(date));

  // One row a month is what makes a month's volume the rate times the month's
  // days. The same request answers per day on a well whose products mix time
  // steps, and that row's rate times a whole month would be some thirty times
  // the truth, printed as a plain number.
  const monthly = calendars.every(
    (calendar, i) =>
      i === 0 ||
      calendar === null ||
      calendars[i - 1] === null ||
      calendar.year !== calendars[i - 1]?.year ||
      calendar.month !== calendars[i - 1]?.month
  );

  // A calendar-day rate is the month's volume over the month's calendar days,
  // so the volume comes back from the rate exactly: 1.0323 bbl/d across
  // January's 31 days is the 32 bbl the service reports for the same month.
  // An axis that is not per day has no such identity, so it carries no volume
  // rather than a number thirty times its own.
  const volumes: ByDate[] = rates.map((byDate, product) => {
    if (!monthly || !rateUnits[product].perDay) return new Map<AxisKey, Maybe<number>>();
    return new Map(
      dates.map((date, i) => [
        date,
        volumeFromCalendarDayRate(byDate.get(date) ?? null, calendars[i])
      ])
    );
  });

  // Months on production: the calendar distance from the first month the
  // response reports, so a month it leaves out still advances the count.
  const first = calendars.find((calendar) => calendar !== null) ?? null;
  const monthsIndex = calendars.map((calendar, i) =>
    calendar && first
      ? (calendar.year - first.year) * 12 + (calendar.month - first.month) + 1
      : i + 1
  );

  const units = { ...DEFAULT_ARRAY_UNITS };
  PRODUCTS.forEach((product, i) => {
    units[product] = {
      volume: rateUnits[i].baseLabel,
      rate: rateUnits[i].label
    };
  });

  return {
    rates,
    volumes,
    monthsIndex,
    dates,
    calendars,
    hasProduction: rateSeries.some((s) => (s?.y?.length ?? 0) > 0),
    units
  };
};

/**
 * How long the panel waits on a filter id before it says so. The id lands
 * within a second or two of a dashboard settling; a card left on a skeleton
 * past this looks like a hung fetch, and there is no failure signal on the
 * filter state to key off instead.
 */
const FILTER_WAIT_MS = 15000;

/**
 * The array's numbers come from the same multi-phase contract the chart uses,
 * on a fixed request so the columns cannot change shape when the user changes
 * the chart. One rate-vs-date response answers the whole table: it carries the
 * month each row is labelled with, the rates, and - across the month's calendar
 * days - the volumes.
 */
export function useProductionArrayData(
  uwi: Maybe<string>,
  enabled: boolean
): ProductionArrayResult {
  const { filterId, userForecastSettings, forecastMode } = useSelector(
    selectRequestInputs,
    shallowEqual
  );

  // The filter id is empty on first paint, so the query cannot run yet. That is
  // not an answer of "no production" — see the isLoading note below.
  const queryEnabled = enabled && !!uwi && !!filterId?.id;
  const waitingForFilter = enabled && !!uwi && !filterId?.id;

  // A filter id that never arrives leaves the query idle for ever, and an idle
  // query is reported as loading below — a skeleton with nothing behind it.
  const [filterGaveUp, setFilterGaveUp] = useState(false);
  useEffect(() => {
    if (!waitingForFilter) {
      setFilterGaveUp(false);
      return;
    }
    const timer = setTimeout(() => setFilterGaveUp(true), FILTER_WAIT_MS);
    return () => clearTimeout(timer);
  }, [waitingForFilter]);

  const chartNormalizeBy = useWellCardPanelStore(selectWellCardChartNormalizeBy);

  const queryKey = (normalizeBy: Maybe<INormalizeByInput>) => [
    "well-card-panel:production-array",
    uwi,
    filterId?.id,
    forecastMode,
    userForecastSettings,
    normalizeBy
  ];

  const fetchArray = (normalizeBy: Maybe<INormalizeByInput>) => async () => {
    const chartSetting: Omit<IMultiPhaseSettingInput, "chartType"> = {
      rateType: "Calendar",
      normalizeBy: normalizeBy ?? NOT_NORMALIZED,
      useForecastStartDate: false,
      forecastMode
    };

    const rate = await postMultiPhase(
      buildRequest(
        uwi,
        filterId?.id,
        ALL_CHART_TYPES.RateDate.id,
        chartSetting,
        userForecastSettings
      )
    );
    const parsed = {
      ...parseResponse(rate),
      uwi,
      normalizedBy: normalizeBy?.displayName
    };
    // The title says "per 100 m" either way; only the divisor says it was applied.
    return normalizeBy && rate?.normalizeData?.[uwi] == null
      ? { ...parsed, notNormalizable: true }
      : parsed;
  };

  // Unnormalized, always: the panel mounts the chart off this answer, and a
  // well with no normalize value answers normalized with no series - the
  // chart would unmount, drop its setting, and remount in a loop.
  const base = useQuery(queryKey(null), fetchArray(null), {
    enabled: queryEnabled,
    retry: false,
    keepPreviousData: true
  });

  // The rows follow the chart. Un-normalized this is the base query's own key,
  // so react-query shares the entry and sends nothing extra.
  // Fresh for this observer only: toggling back to a cached key must not
  // refetch it. The base observer keeps the default, so a remount still does.
  const normalized = useQuery(queryKey(chartNormalizeBy), fetchArray(chartNormalizeBy), {
    enabled: queryEnabled,
    retry: false,
    keepPreviousData: true,
    staleTime: Infinity
  });

  const { isIdle, isLoading, isError, refetch } = base;
  // The normalized request is the slower one (the service resolves divisors
  // across the filter first), so after a well switch the base query can settle
  // while these rows still belong to the last well.
  const rowsForOtherWell = normalized.isPreviousData && normalized.data?.uwi !== uwi;
  const rowsFailed = !!chartNormalizeBy && normalized.isError;
  const notNormalizable = !!normalized.data?.notNormalizable && !rowsForOtherWell;
  const data =
    rowsFailed || normalized.data?.notNormalizable ? undefined : normalized.data;
  // A new target dims the card until its own rows arrive. A normalize toggle on
  // the same well does not: the previous rows keep their own headers meanwhile.
  const isPreviousData = base.isPreviousData || rowsForOtherWell;

  const rows = useMemo<ProductionArrayRow[]>(() => {
    if (!data) return [];

    return data.dates.map((date, i) => {
      const month = data.monthsIndex[i];
      return {
        date,
        monthsIndex: month,
        calendar: data.calendars[i],
        oilVolume: valueAt(data.volumes[0], date),
        oilRate: valueAt(data.rates[0], date),
        gasVolume: valueAt(data.volumes[1], date),
        gasRate: valueAt(data.rates[1], date),
        waterVolume: valueAt(data.volumes[2], date),
        waterRate: valueAt(data.rates[2], date)
      };
    });
  }, [data]);

  // Waiting again is the only retry a missing filter id has, so the button
  // restarts the wait rather than refetching a query that cannot run.
  const refetchNormalized = normalized.refetch;
  const retry = useCallback(() => {
    setFilterGaveUp(false);
    refetch();
    if (chartNormalizeBy) refetchNormalized();
  }, [refetch, refetchNormalized, chartNormalizeBy]);

  return {
    rows,
    units: data?.units ?? DEFAULT_ARRAY_UNITS,
    // A failed fetch is not an absence of production, so the empty state must
    // not claim it is.
    hasProduction: !isError && !filterGaveUp && (base.data?.hasProduction ?? false),
    // react-query calls a query that has never been allowed to run "idle", not
    // "loading". Reported as settled, an idle query renders the designed
    // "No production reported for this well" over a well nobody has asked
    // about yet.
    isLoading: !filterGaveUp && (isLoading || (isIdle && enabled && !!uwi)),
    isError: isError || filterGaveUp,
    filterUnavailable: filterGaveUp,
    isPreviousData,
    rowsFailed,
    notNormalizable,
    normalizedBy: data?.normalizedBy ?? null,
    refetch: retry
  };
}
